import { FormEvent, useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { ITAAS_INSTITUTION } from "@itaas/pep-contracts";
import { PepLogo } from "../brand/PepLogo";
import { InstitutionalJsonLd } from "../trust/InstitutionalTrust";
import { usePepI18n } from "../../i18n/usePepI18n";
import { useTheme } from "../../providers/PepProviders";
import { SUITE_URL } from "../../config";
import { LanguageSwitcher } from "./LanguageSwitcher";

type NavKey =
  | "nav.products"
  | "nav.solutions"
  | "nav.industries"
  | "nav.package"
  | "nav.why"
  | "nav.docs"
  | "nav.knowledge"
  | "nav.marketplace"
  | "nav.partners"
  | "nav.affiliation"
  | "nav.about"
  | "nav.contact";

const PRIMARY_NAV: { to: string; key: NavKey }[] = [
  { to: "/products", key: "nav.products" },
  { to: "/solutions", key: "nav.solutions" },
  { to: "/industries", key: "nav.industries" },
  { to: "/configure", key: "nav.package" },
  { to: "/knowledge", key: "nav.knowledge" },
];

const MORE_NAV: { to: string; key: NavKey }[] = [
  { to: "/docs", key: "nav.docs" },
  { to: "/about", key: "nav.about" },
  { to: "/why-itaas", key: "nav.why" },
  { to: "/marketplace", key: "nav.marketplace" },
  { to: "/partners", key: "nav.partners" },
  { to: "/affiliation", key: "nav.affiliation" },
  { to: "/contact", key: "nav.contact" },
];

function SearchIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.04.045.084.085l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.1-.172m-5.48 1.095a3.5 3.5 0 1 1 0-7 3.5 3.5 0 0 1 0 7" />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M11 6a3 3 0 1 1-6 0 3 3 0 0 1 6 0" />
      <path
        fillRule="evenodd"
        d="M0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8m8-7a7 7 0 0 0-5.468 11.37C3.242 11.226 4.805 10 8 10s4.757 1.225 5.468 2.37A7 7 0 0 0 8 1"
      />
    </svg>
  );
}

function NavMoreMenu({ items, onNavigate }: { items: typeof MORE_NAV; onNavigate: () => void }) {
  const { t } = usePepI18n();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [open]);

  return (
    <li className="pep-nav-more nav-item" ref={rootRef}>
      <button
        type="button"
        className="nav-link pep-nav-link pep-nav-more__toggle"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="true"
      >
        {t("nav.more")}
        <span className="pep-nav-more__chevron" aria-hidden="true" />
      </button>
      {open ? (
        <ul className="pep-nav-more__menu list-unstyled mb-0">
          {items.map((item) => (
            <li key={item.to}>
              <NavLink
                className="pep-nav-more__link"
                to={item.to}
                onClick={() => {
                  setOpen(false);
                  onNavigate();
                }}
              >
                {t(item.key)}
              </NavLink>
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

export function SiteHeader() {
  const { theme, toggle } = useTheme();
  const { t } = usePepI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (searchOpen) {
      searchInputRef.current?.focus();
    }
  }, [searchOpen]);

  function onSearch(e: FormEvent) {
    e.preventDefault();
    if (!searchQ.trim()) return;
    navigate(`/search?q=${encodeURIComponent(searchQ.trim())}`);
    setMenuOpen(false);
    setSearchOpen(false);
  }

  function closeMenu() {
    setMenuOpen(false);
  }

  return (
    <header className="pep-header sticky-top">
      <nav className="container pep-header__nav" aria-label="Main">
        <div className="pep-header__bar">
          <Link className="pep-brand pep-header__brand" to="/" onClick={closeMenu}>
            <PepLogo variant="header" />
          </Link>

          <button
            type="button"
            className={`pep-nav-toggler${menuOpen ? " is-active" : ""}`}
            onClick={() => setMenuOpen((value) => !value)}
            aria-expanded={menuOpen}
            aria-controls="pepNavPanel"
            aria-label={t("nav.menu")}
          >
            <span className="pep-nav-toggler__lines" aria-hidden="true" />
          </button>
        </div>

        <div
          id="pepNavPanel"
          className={`pep-header__panel${menuOpen ? " is-open" : ""}`}
        >
          <ul className="navbar-nav pep-header__links">
            {PRIMARY_NAV.map((item) => (
              <li className="nav-item" key={item.to}>
                <NavLink className="nav-link pep-nav-link" to={item.to} onClick={closeMenu}>
                  {t(item.key)}
                </NavLink>
              </li>
            ))}
            <NavMoreMenu items={MORE_NAV} onNavigate={closeMenu} />
            {MORE_NAV.map((item) => (
              <li className="nav-item pep-header__links-extra" key={item.to}>
                <NavLink className="nav-link pep-nav-link" to={item.to} onClick={closeMenu}>
                  {t(item.key)}
                </NavLink>
              </li>
            ))}
          </ul>

          <div className="pep-header__aside">
            <form
              className={`pep-header__search${searchOpen ? " is-open" : ""}`}
              onSubmit={onSearch}
              role="search"
            >
              <button
                type="button"
                className="pep-header__tool-btn d-lg-none"
                onClick={() => setSearchOpen((value) => !value)}
                aria-label={t("nav.search")}
                aria-expanded={searchOpen}
              >
                <SearchIcon />
              </button>
              <div className="pep-header__search-field">
                <SearchIcon />
                <input
                  ref={searchInputRef}
                  className="form-control form-control-sm border-0 shadow-none bg-transparent"
                  placeholder={t("nav.searchPlaceholder")}
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                  aria-label={t("nav.search")}
                />
              </div>
            </form>

            <div className="pep-header__toolbar">
              <button
                type="button"
                className={`pep-header__tool-btn pep-header__search-toggle d-none d-lg-inline-flex${
                  searchOpen ? " is-active" : ""
                }`}
                onClick={() => setSearchOpen((value) => !value)}
                aria-label={t("nav.search")}
                aria-expanded={searchOpen}
              >
                <SearchIcon />
              </button>

              <LanguageSwitcher variant="compact" />

              <button
                type="button"
                className="pep-header__tool-btn"
                onClick={toggle}
                aria-label={t("nav.toggleTheme")}
              >
                {theme === "light" ? "🌙" : "☀️"}
              </button>

              <a
                className="pep-header__tool-btn"
                href={SUITE_URL}
                aria-label={t("nav.login")}
                title={t("nav.login")}
              >
                <UserIcon />
              </a>

              <div className="pep-header__cta-group">
                <Link
                  className="btn btn-primary btn-sm pep-header__cta"
                  to="/configure"
                  onClick={closeMenu}
                >
                  {t("nav.compose")}
                </Link>
                <Link
                  className="btn btn-outline-primary btn-sm pep-header__cta pep-header__cta--demo"
                  to="/contact?type=demo"
                  onClick={closeMenu}
                >
                  {t("nav.demo")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
}

export function SiteFooter() {
  const { t, locale } = usePepI18n();
  const en = locale === "en";
  const year = new Date().getFullYear();

  return (
    <footer className="pep-footer mt-auto py-5">
      <div className="container">
        <div className="row g-4">
          <div className="col-lg-4">
            <h6 className="fw-bold mb-2 d-flex align-items-center gap-2">
              <PepLogo variant="footer" />
              <span className="visually-hidden">ITaaS Suite</span>
            </h6>
            <p className="text-secondary small mb-2">{t("footer.tagline")}</p>
            <p className="small mb-1">
              <strong>{ITAAS_INSTITUTION.brandName}</strong>
              {" — "}
              {en ? "publisher of ITaaS Suite" : "éditeur d’ITaaS Suite"}
            </p>
            <p className="small text-secondary mb-1">
              {ITAAS_INSTITUTION.address.addressLocality} — RDC
            </p>
            <p className="small text-secondary mb-1">
              {ITAAS_INSTITUTION.address.streetAddress}, {ITAAS_INSTITUTION.address.addressRegion}
            </p>
            <p className="small mb-1">
              <a href={`mailto:${ITAAS_INSTITUTION.email}`}>{ITAAS_INSTITUTION.email}</a>
            </p>
            <p className="small mb-0">
              <a href={`tel:${ITAAS_INSTITUTION.telephoneTel}`}>{ITAAS_INSTITUTION.telephone}</a>
            </p>
          </div>
          <div className="col-6 col-md-2">
            <h6 className="small text-uppercase text-secondary">{t("footer.products")}</h6>
            <ul className="list-unstyled small">
              <li>
                <Link to="/products">{t("footer.catalog")}</Link>
              </li>
              <li>
                <Link to="/solutions">{t("footer.solutions")}</Link>
              </li>
              <li>
                <Link to="/industries">{t("footer.industries")}</Link>
              </li>
              <li>
                <Link to="/marketplace">{t("footer.marketplace")}</Link>
              </li>
            </ul>
          </div>
          <div className="col-6 col-md-2">
            <h6 className="small text-uppercase text-secondary">{t("footer.resources")}</h6>
            <ul className="list-unstyled small">
              <li>
                <Link to="/knowledge">{t("footer.knowledge")}</Link>
              </li>
              <li>
                <Link to="/why-itaas">{t("footer.why")}</Link>
              </li>
              <li>
                <Link to="/docs">{t("footer.documentation")}</Link>
              </li>
              <li>
                <Link to="/configure">{t("footer.package")}</Link>
              </li>
            </ul>
          </div>
          <div className="col-md-4">
            <h6 className="small text-uppercase text-secondary">
              {en ? "Company" : "Société"}
            </h6>
            <ul className="list-unstyled small mb-3">
              <li>
                <Link to="/about">{en ? "About" : "À propos"}</Link>
              </li>
              <li>
                <Link to="/contact">{t("nav.contact")}</Link>
              </li>
              <li>
                <Link to="/legal">{en ? "Legal notice" : "Mentions légales"}</Link>
              </li>
              <li>
                <Link to="/privacy">{en ? "Privacy" : "Confidentialité"}</Link>
              </li>
              <li>
                <a href={ITAAS_INSTITUTION.institutionalWebsite} rel="noopener noreferrer">
                  ITaaS RDC.com
                </a>
              </li>
            </ul>
            <p className="small text-secondary mb-1">
              © {year} {ITAAS_INSTITUTION.brandName}. {en ? "All rights reserved." : "Tous droits réservés."}
            </p>
            <p className="small text-secondary mb-0">{t("footer.license")}</p>
          </div>
        </div>
      </div>
    </footer>
  );
}

export function SiteLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="d-flex flex-column min-vh-100">
      <InstitutionalJsonLd />
      <SiteHeader />
      <main className="flex-grow-1">{children}</main>
      <SiteFooter />
    </div>
  );
}
